import {OrdersListDTO} from "@dto/OrdersListDTO";
import {TabLeaderElection} from "@features/orders-poller/tab-leader-election";
import {
    createOrdersPollerChannel,
    findLocalStorage,
    OrdersPollerChannel,
    OrdersPollerMessage
} from "@features/orders-poller/orders-poller-channel";

type ElectionEntry = {
    election: TabLeaderElection
    usersCount: number
}

/**
 * One leader election per poller key and ONE channel per tab, shared by every useOrdersList of the tab
 * (the header, the list, every order card): the tab joins the election when the first of them mounts
 * and leaves it when the last one unmounts or the tab closes.
 */
const TAB_ID = `${Date.now()}-${Math.random().toString(36).slice(2)}`

const elections = new Map<string, ElectionEntry>()

let channel: OrdersPollerChannel | null | undefined = undefined

const getChannel = (): OrdersPollerChannel | null => {
    if (channel !== undefined) return channel

    channel = createOrdersPollerChannel()
    channel?.subscribe(onChannelMessage)
    if (typeof window !== 'undefined') {
        // pagehide also fires where beforeunload does not (iOS Safari, bfcache).
        window.addEventListener('pagehide', pauseAllElections)
        // A page restored from the back-forward cache keeps its mounted hooks: it joins the elections again.
        window.addEventListener('pageshow', resumeAllElections)
    }

    return channel
}

const onChannelMessage = (message: OrdersPollerMessage): void => {
    if (message.type !== 'leader-released') return

    // The leader tab closed: take over now instead of waiting for its lease to expire.
    elections.get(message.pollerKey)?.election.tryToBecomeLeader()
}

const pauseAllElections = (): void => {
    elections.forEach((entry, pollerKey) => stopElection(pollerKey, entry.election))
}

const resumeAllElections = (event: PageTransitionEvent): void => {
    if (!event.persisted) return

    elections.forEach(entry => entry.election.start())
}

const stopElection = (pollerKey: string, election: TabLeaderElection): void => {
    const wasLeader = election.stop()
    if (!wasLeader) return

    getChannel()?.publish({type: 'leader-released', pollerKey})
}

export const joinLeaderElection = (pollerKey: string): TabLeaderElection => {
    getChannel()

    const existingEntry = elections.get(pollerKey)
    if (existingEntry) {
        existingEntry.usersCount++
        return existingEntry.election
    }

    const election = new TabLeaderElection(pollerKey, TAB_ID, findLocalStorage())
    elections.set(pollerKey, {election, usersCount: 1})
    election.start()

    return election
}

export const leaveLeaderElection = (pollerKey: string): void => {
    const entry = elections.get(pollerKey)
    if (!entry) return

    entry.usersCount--
    if (entry.usersCount > 0) return

    elections.delete(pollerKey)
    stopElection(pollerKey, entry.election)
}

export const publishOrdersList = (pollerKey: string, ordersList: OrdersListDTO): void => {
    getChannel()?.publish({type: 'orders-list', pollerKey, ordersList})
}

export const subscribeToOrdersLists = (
    pollerKey: string,
    onOrdersList: (ordersList: OrdersListDTO) => void,
): () => void => {
    const tabChannel = getChannel()
    if (tabChannel === null) return () => null

    return tabChannel.subscribe(message => {
        if (message.type !== 'orders-list') return
        if (message.pollerKey !== pollerKey) return
        onOrdersList(message.ordersList)
    })
}

/**
 * Tests only: forget the elections and the channel of this "tab".
 */
export const resetOrdersPollerForTests = (): void => {
    pauseAllElections()
    elections.clear()
    if (typeof window !== 'undefined') {
        window.removeEventListener('pagehide', pauseAllElections)
        window.removeEventListener('pageshow', resumeAllElections)
    }
    channel = undefined
}
